<script setup lang="ts">
import { ref, computed, watch } from "vue";
import useApiStore from "@/stores/api";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import { useI18n } from "vue-i18n";
import { ElButtonGroup } from "element-plus";

export interface Props {
  inPhoto?: string;
  outPhoto?: string;
}

const props = defineProps<Props>();

const { t } = useI18n();
const apiStore = useApiStore();

const activePhoto = ref(props.inPhoto || props.outPhoto);
const showInOutToggle = computed(() => props.inPhoto && props.outPhoto);

const src = computed(() =>
  activePhoto.value?.startsWith("//") ||
  activePhoto.value?.startsWith("https://")
    ? activePhoto.value
    : apiStore.getEndpoint("platform", `/mm/${activePhoto.value}`)
);

const inOutPhoto = computed(() => props.inPhoto || props.outPhoto);

const click = () => window.open(src.value);

watch(inOutPhoto, (newVal) => {
  activePhoto.value = newVal;
});
</script>

<template>
  <div class="container">
    <el-button-group
      v-if="showInOutToggle"
      class="toggle-buttons"
    >
      <CoreButton
        class="toggle-button"
        size="default"
        :type="activePhoto === props.inPhoto ? 'primary' : undefined"
        @click="activePhoto = props.inPhoto"
      >
        {{ t("inOutImage.in") }}
      </CoreButton>
      <CoreButton
        class="toggle-button"
        size="default"
        :type="activePhoto === props.outPhoto ? 'primary' : undefined"
        @click="activePhoto = props.outPhoto"
      >
        {{ t("inOutImage.out") }}
      </CoreButton>
    </el-button-group>
    <CoreButton
      v-if="activePhoto"
      class="download-button"
      :icon="['fal', 'download']"
      size="default"
      circle
      @click="click"
    />
    <img
      v-if="activePhoto"
      :src="src"
      alt="in-out-image"
      class="in-out-image"
    />
  </div>
</template>

<style lang="scss" scoped>
.container {
  overflow: hidden;
  position: relative;
}

.toggle-buttons,
.download-button {
  position: absolute;
  top: 15px;
}

.download-button {
  right: 15px;
}

.toggle-buttons {
  left: 15px;
}

.toggle-button {
  min-width: 70px;
}

.in-out-image {
  aspect-ratio: 1 / 1;
  border-radius: 5px;
  object-fit: cover;
  width: 100%;
}
</style>
